"use client";

import { useState, useEffect, useRef } from "react";
import Icon from "@/components/ui/AppIcon";

const businessTypes = [
  "Veterinary Clinic",
  "DogFob Trainer",
  "Groomer",
  "Pet Store",
  "DogFob Walker",
  "Boarding Facility",
  "Pet Photographer",
  "Other",
];

interface FormState {
  businessName: string;
  businessType: string;
  contactName: string;
  email: string;
  phone: string;
  city: string;
  message: string;
}

interface FormErrors {
  businessName?: string;
  businessType?: string;
  contactName?: string;
  email?: string;
  phone?: string;
  city?: string;
  message?: string;
}

const initialForm: FormState = {
  businessName: "",
  businessType: "",
  contactName: "",
  email: "",
  phone: "",
  city: "",
  message: "",
};

export default function PartnerApplicationForm() {
  const [form, setForm] = useState<FormState>(initialForm);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<keyof FormState, boolean>>({
    businessName: false,
    businessType: false,
    contactName: false,
    email: false,
    phone: false,
    city: false,
    message: false,
  });
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target
              .querySelectorAll(".scroll-fade-up, .scroll-fade-left, .scroll-fade-right")
              .forEach((el, i) => {
                setTimeout(() => el.classList.add("is-visible"), i * 90);
              });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const validateField = (name: keyof FormState, value: string): string | undefined => {
    switch (name) {
      case "businessName":
        if (!value.trim()) return "Business name is required";
        if (value.trim().length < 2) return "Business name must be at least 2 characters";
        break;
      case "businessType":
        if (!value) return "Please select a business type";
        break;
      case "contactName":
        if (!value.trim()) return "Contact name is required";
        if (value.trim().length < 2) return "Contact name must be at least 2 characters";
        break;
      case "email":
        if (!value.trim()) return "Email is required";
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) return "Please enter a valid email address";
        break;
      case "phone":
        if (!value.trim()) return "Phone number is required";
        const phoneRegex = /^[\d\s()+-]+$/;
        if (!phoneRegex.test(value)) return "Please enter a valid phone number";
        if (value.replace(/\D/g, "").length < 10) return "Phone number must be at least 10 digits";
        break;
      case "city":
        if (!value.trim()) return "City is required";
        break;
      case "message":
        if (value.trim() && value.trim().length < 10) return "Message must be at least 10 characters";
        break;
    }
    return undefined;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    
    if (touched[name as keyof FormState]) {
      const error = validateField(name as keyof FormState, value);
      setErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  const handleBlur = (name: keyof FormState) => {
    setTouched((prev) => ({ ...prev, [name]: true }));
    const error = validateField(name, form[name]);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate all fields
    const newErrors: FormErrors = {};
    (Object.keys(form) as Array<keyof FormState>).forEach((key) => {
      const error = validateField(key, form[key]);
      if (error) newErrors[key] = error;
    });
    
    setErrors(newErrors);
    setTouched({
      businessName: true,
      businessType: true,
      contactName: true,
      email: true,
      phone: true,
      city: true,
      message: true,
    });
    
    // Only submit if no errors
    if (Object.keys(newErrors).length === 0) {
      // Backend connection point: POST form data to /api/partner-application
      setSubmitted(true);
    }
  };

  const getFieldClassName = (fieldName: keyof FormState) => {
    const baseClass = "form-input";
    if (touched[fieldName] && errors[fieldName]) {
      return `${baseClass} border-red-400 focus:border-red-500 focus:ring-red-200`;
    }
    if (touched[fieldName] && !errors[fieldName] && form[fieldName]) {
      return `${baseClass} border-green-400 focus:border-green-500 focus:ring-green-200`;
    }
    return baseClass;
  };

  const renderFieldError = (fieldName: keyof FormState) => {
    if (touched[fieldName] && errors[fieldName]) {
      return (
        <div className="flex items-center gap-1.5 text-xs text-red-600 mt-1">
          <Icon name="ExclamationCircleIcon" size={14} />
          <span>{errors[fieldName]}</span>
        </div>
      );
    }
    if (touched[fieldName] && !errors[fieldName] && form[fieldName]) {
      return (
        <div className="flex items-center gap-1.5 text-xs text-green-600 mt-1">
          <Icon name="CheckCircleIcon" size={14} />
          <span>Looks good!</span>
        </div>
      );
    }
    return null;
  };

  return (
    <section
      id="apply"
      ref={sectionRef}
      className="py-24 bg-white dark:bg-[#0f172a]"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-14 items-start">
          {/* Left: Value props */}
          <div className="flex flex-col gap-7 scroll-fade-left lg:sticky lg:top-24">
            <span className="teal-badge">Apply Now</span>
            <h2 className="text-4xl sm:text-[42px] font-extrabold text-[#1A2B2B] dark:text-slate-100 tracking-tight leading-tight">
              Become a{" "}
              <span className="gradient-text">DogFob Partner</span>
              <br />
              today.
            </h2>
            <p className="text-[#4A6363] dark:text-slate-400 leading-relaxed text-lg">
              Applications are reviewed within 1 business day. Once approved, onboarding takes a single afternoon.
            </p>

            <div className="flex flex-col gap-4">
              {[
                {
                  icon: "CheckBadgeIcon",
                  title: "Verified Partner badge",
                  desc: "Your business gets a verified badge visible to all DogFob pet owners.",
                  color: "#0EA5A0",
                  bg: "#E0F7F6",
                },
                {
                  icon: "MagnifyingGlassIcon",
                  title: "Discoverable on the partner map",
                  desc: "Pet owners near you can find, review, and book your services directly.",
                  color: "#7C3AED",
                  bg: "#EDE9FE",
                },
                {
                  icon: "CurrencyDollarIcon",
                  title: "No listing fees",
                  desc: "Your basic partner profile is free. Premium tools are available on a simple monthly plan.",
                  color: "#059669",
                  bg: "#DCFCE7",
                },
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-4">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ background: item.bg }}
                  >
                    <Icon
                      name={item.icon as "CheckBadgeIcon"}
                      size={18}
                      style={{ color: item.color }}
                    />
                  </div>
                  <div>
                    <h4 className="text-sm font-bold text-[#1A2B2B] dark:text-slate-100 font-heading mb-0.5">
                      {item.title}
                    </h4>
                    <p className="text-sm text-[#4A6363] dark:text-slate-400">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Trust note */}
            <div className="flex items-center gap-3 pt-4 border-t border-[#E2EDED] dark:border-slate-700">
              <Icon name="LockClosedIcon" size={16} className="text-teal-500" />
              <p className="text-sm text-[#8AA4A4] dark:text-slate-500">
                Your application data is encrypted and never shared with third parties.
              </p>
            </div>
          </div>

          {/* Right: Form */}
          <div className="scroll-fade-right">
            {submitted ? (
              <div className="feature-card p-12 flex flex-col items-center gap-5 text-center">
                <div className="w-20 h-20 rounded-full bg-[#E0F7F6] flex items-center justify-center animate-pulse-glow">
                  <Icon
                    name="CheckCircleIcon"
                    size={40}
                    className="text-teal-500"
                    variant="solid"
                  />
                </div>
                <h3 className="text-2xl font-extrabold text-[#1A2B2B] dark:text-slate-100 font-heading">
                  Application received!
                </h3>
                <p className="text-[#4A6363] dark:text-slate-400 leading-relaxed max-w-sm">
                  Thank you, <strong>{form.businessName}</strong>. Our team will review your application and reach out to{" "}
                  <strong>{form.email}</strong> within 1 business day.
                </p>
                <div className="flex flex-col gap-2 mt-2">
                  <span className="teal-badge">
                    <Icon name="ClockIcon" size={12} />
                    Review within 24 hours
                  </span>
                  <div className="flex items-center gap-2 text-sm text-[#4A6363]">
                    <Icon name="EnvelopeIcon" size={16} className="text-teal-500" />
                    <span>Confirmation email sent to {form.email}</span>
                  </div>
                </div>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="feature-card p-8 flex flex-col gap-5"
              >
                <h3 className="text-xl font-bold text-[#1A2B2B] dark:text-slate-100 font-heading mb-1">
                  Partner Application
                </h3>

                {/* Business name */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-[#4A6363] dark:text-slate-400 uppercase tracking-wide">
                    Business name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="businessName"
                    value={form.businessName}
                    onChange={handleChange}
                    onBlur={() => handleBlur("businessName")}
                    placeholder="e.g. Riverside Animal Clinic"
                    className={getFieldClassName("businessName")}
                    aria-label="Business name"
                    aria-invalid={touched.businessName && !!errors.businessName}
                  />
                  {renderFieldError("businessName")}
                </div>

                {/* Business type */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-[#4A6363] dark:text-slate-400 uppercase tracking-wide">
                    Business type <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="businessType"
                    value={form.businessType}
                    onChange={handleChange}
                    onBlur={() => handleBlur("businessType")}
                    className={getFieldClassName("businessType")}
                    aria-label="Business type"
                    aria-invalid={touched.businessType && !!errors.businessType}
                  >
                    <option value="" disabled>
                      Select your business type
                    </option>
                    {businessTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                  {renderFieldError("businessType")}
                </div>

                {/* Contact name + email */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-[#4A6363] dark:text-slate-400 uppercase tracking-wide">
                      Contact name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="contactName"
                      value={form.contactName}
                      onChange={handleChange}
                      onBlur={() => handleBlur("contactName")}
                      placeholder="Dr. Jane Smith"
                      className={getFieldClassName("contactName")}
                      aria-label="Contact person name"
                      aria-invalid={touched.contactName && !!errors.contactName}
                    />
                    {renderFieldError("contactName")}
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-[#4A6363] dark:text-slate-400 uppercase tracking-wide">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      onBlur={() => handleBlur("email")}
                      placeholder="hello@yourclinic.com"
                      className={getFieldClassName("email")}
                      aria-label="Email address"
                      aria-invalid={touched.email && !!errors.email}
                    />
                    {renderFieldError("email")}
                  </div>
                </div>

                {/* Phone + City */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-[#4A6363] dark:text-slate-400 uppercase tracking-wide">
                      Phone <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      onBlur={() => handleBlur("phone")}
                      placeholder="(555) 123-4567"
                      className={getFieldClassName("phone")}
                      aria-label="Phone number"
                      aria-invalid={touched.phone && !!errors.phone}
                    />
                    {renderFieldError("phone")}
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-semibold text-[#4A6363] dark:text-slate-400 uppercase tracking-wide">
                      City <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={form.city}
                      onChange={handleChange}
                      onBlur={() => handleBlur("city")}
                      placeholder="San Francisco"
                      className={getFieldClassName("city")}
                      aria-label="City"
                      aria-invalid={touched.city && !!errors.city}
                    />
                    {renderFieldError("city")}
                  </div>
                </div>

                {/* Message */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-[#4A6363] dark:text-slate-400 uppercase tracking-wide">
                    Tell us about your business (optional)
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    onBlur={() => handleBlur("message")}
                    rows={4}
                    placeholder="Share what makes your business special..."
                    className={getFieldClassName("message")}
                    aria-label="Additional message"
                    aria-invalid={touched.message && !!errors.message}
                  />
                  {renderFieldError("message")}
                </div>

                <button type="submit" className="btn-primary w-full justify-center py-4">
                  <Icon name="PaperAirplaneIcon" size={18} />
                  Submit Application
                </button>

                <p className="text-xs text-[#8AA4A4] dark:text-slate-500 text-center">
                  By submitting, you agree to our partner terms and privacy policy.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}